import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  InputNumber,
  Input,
  message,
  Popconfirm,
  Space,
  Tabs,
  Select,
  Tooltip,
} from 'antd';
import { EditIcon, XIcon, HistoryIcon, ActivityIcon } from 'lucide-react';
import dayjs from 'dayjs';
import EmployeeFundServiceInstance, {
  EmployeeFundService,
} from '../../service/employee-service/Employee-fund.service';
import type { FundTransferResponse } from '../../service/employee-service/Employee-fund.service';
import {
  EmployeeFundRecord,
  CHANGE_TYPE_LABELS,
  CHANGE_TYPE_COLORS,
} from '../../model/EmployeeFundRecord.model';

interface TransferHistoryProps {
  transfers: FundTransferResponse[];
  onTransferUpdate: () => void;
  currentBalance: number;
  employeeId: number;
}

const { TabPane } = Tabs;
const { Option } = Select;

const TransferHistory: React.FC<TransferHistoryProps> = ({
  transfers,
  onTransferUpdate,
  currentBalance,
  employeeId,
}) => {
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingTransfer, setEditingTransfer] =
    useState<FundTransferResponse | null>(null);
  const [editForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fundRecords, setFundRecords] = useState<EmployeeFundRecord[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [selectedChangeType, setSelectedChangeType] = useState<string>('all');

  // Fetch employee fund records
  const fetchFundRecords = async () => {
    try {
      setRecordsLoading(true);
      const records =
        await EmployeeFundService.getEmployeeFundRecords(employeeId);
      setFundRecords(records);
    } catch (error: any) {
      console.error('Error fetching fund records:', error);
      message.error('Có lỗi xảy ra khi tải lịch sử biến động');
    } finally {
      setRecordsLoading(false);
    }
  };

  useEffect(() => {
    if (employeeId) {
      fetchFundRecords();
    }
  }, [employeeId]);

  const handleEdit = (transfer: FundTransferResponse) => {
    setEditingTransfer(transfer);
    editForm.setFieldsValue({
      amount: transfer.amount,
      description: transfer.description,
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async (values: any) => {
    if (!editingTransfer) return;

    try {
      setLoading(true);
      await EmployeeFundServiceInstance.editFundTransfer(
        editingTransfer.id,
        values.amount,
        values.description
      );

      message.success('Cập nhật chuyển tiền thành công!');
      setEditModalVisible(false);
      setEditingTransfer(null);
      editForm.resetFields();
      onTransferUpdate();
      fetchFundRecords(); // Refresh fund records
    } catch (error: any) {
      console.error('Edit transfer error:', error);
      message.error(error.response?.data || 'Có lỗi xảy ra khi cập nhật');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (transferId: number) => {
    try {
      setLoading(true);
      await EmployeeFundServiceInstance.cancelFundTransfer(transferId);

      message.success('Hủy chuyển tiền thành công!');
      onTransferUpdate();
      fetchFundRecords(); // Refresh fund records
    } catch (error: any) {
      console.error('Cancel transfer error:', error);
      message.error(
        error.response?.data || 'Có lỗi xảy ra khi hủy chuyển tiền'
      );
    } finally {
      setLoading(false);
    }
  };

  // Columns for fund transfer table
  const transferColumns = [
    {
      title: 'Ngày chuyển',
      dataIndex: 'transferDate',
      key: 'transferDate',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm'),
      sorter: (a: FundTransferResponse, b: FundTransferResponse) =>
        dayjs(a.transferDate).unix() - dayjs(b.transferDate).unix(),
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => (
        <span className="font-semibold text-red-600">
          -{amount.toLocaleString('vi-VN')} đ
        </span>
      ),
      sorter: (a: FundTransferResponse, b: FundTransferResponse) =>
        a.amount - b.amount,
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      render: (description: string) => (
        <Tooltip
          title={description && description.length > 40 ? description : null}
        >
          <div
            style={{
              wordWrap: 'break-word',
              whiteSpace: 'normal',
              lineHeight: '1.4',
            }}
          >
            {description || 'Không có mô tả'}
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'COMPLETED' ? 'green' : 'red'}>
          {status === 'COMPLETED' ? 'Hoàn thành' : 'Đã hủy'}
        </Tag>
      ),
    },
    {
      title: 'Số dư sau chuyển',
      dataIndex: 'employeeBalanceAfter',
      key: 'employeeBalanceAfter',
      render: (balance: number) => (
        <span className="text-blue-600">
          {balance.toLocaleString('vi-VN')} đ
        </span>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_: any, record: FundTransferResponse) => (
        <Space>
          {record.status === 'COMPLETED' && (
            <>
              <Button
                type="link"
                size="small"
                icon={<EditIcon className="h-4 w-4" />}
                onClick={() => handleEdit(record)}
              >
                Sửa
              </Button>
              <Popconfirm
                title="Hủy chuyển tiền"
                description="Bạn có chắc chắn muốn hủy giao dịch chuyển tiền này? Tiền sẽ được hoàn lại vào tài khoản của bạn."
                onConfirm={() => handleCancel(record.id)}
                okText="Có"
                cancelText="Không"
              >
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<XIcon className="h-4 w-4" />}
                >
                  Hủy
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  // Columns for fund records table
  const recordColumns = [
    {
      title: 'Thời gian',
      dataIndex: 'dateTime',
      key: 'dateTime',
      render: (date: string) => dayjs(date).format('DD-MM-YYYY  HH:mm:ss'),
      sorter: (a: EmployeeFundRecord, b: EmployeeFundRecord) =>
        dayjs(a.dateTime).unix() - dayjs(b.dateTime).unix(),
    },
    {
      title: 'Loại biến động',
      dataIndex: 'changeType',
      key: 'changeType',
      render: (changeType: string) => (
        <Tag color={CHANGE_TYPE_COLORS[changeType] || 'default'}>
          {CHANGE_TYPE_LABELS[changeType] || changeType}
        </Tag>
      ),
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => (
        <span
          className={`font-semibold ${
            amount >= 0 ? 'text-green-600' : 'text-red-600'
          }`}
        >
          {amount >= 0 ? '+' : ''}
          {amount.toLocaleString('vi-VN')} đ
        </span>
      ),
      sorter: (a: EmployeeFundRecord, b: EmployeeFundRecord) =>
        a.amount - b.amount,
    },
    {
      title: 'Số dư trước',
      dataIndex: 'balanceBefore',
      key: 'balanceBefore',
      render: (balance: number) => (
        <span className="text-gray-600">
          {balance.toLocaleString('vi-VN')} đ
        </span>
      ),
    },
    {
      title: 'Số dư sau',
      dataIndex: 'balanceAfter',
      key: 'balanceAfter',
      render: (balance: number) => (
        <span className="text-blue-600 font-semibold">
          {balance.toLocaleString('vi-VN')} đ
        </span>
      ),
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      width: 400,
      render: (notes: string) => (
        <Tooltip title={notes && notes.length > 50 ? notes : null}>
          <div
            style={{
              wordWrap: 'break-word',
              whiteSpace: 'normal',
              lineHeight: '1.4',
            }}
          >
            {notes || 'Không có ghi chú'}
          </div>
        </Tooltip>
      ),
    },
  ];

  // Filter fund records by change type
  const filteredFundRecords =
    selectedChangeType === 'all'
      ? fundRecords
      : fundRecords.filter(
          (record) => record.changeType === selectedChangeType
        );

  return (
    <>
      <Card
        title={
          <div className="flex items-center gap-2">
            <HistoryIcon className="h-5 w-5" />
            <span>Lịch sử giao dịch</span>
          </div>
        }
      >
        <Tabs defaultActiveKey="transfers">
          <TabPane tab="Chuyển về công ty" key="transfers">
            <Table
              columns={transferColumns}
              dataSource={transfers}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: false,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} của ${total} giao dịch`,
              }}
              loading={loading}
              scroll={{ x: 900 }}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <ActivityIcon className="h-4 w-4 inline mr-1" />
                Tất cả biến động
              </span>
            }
            key="records"
          >
            <div className="mb-4">
              <Space>
                <span>Lọc theo loại:</span>
                <Select
                  value={selectedChangeType}
                  onChange={setSelectedChangeType}
                  style={{ width: 200 }}
                >
                  <Option value="all">Tất cả</Option>
                  {Object.entries(CHANGE_TYPE_LABELS).map(([key, label]) => (
                    <Option key={key} value={key}>
                      {label}
                    </Option>
                  ))}
                </Select>
              </Space>
            </div>

            <Table
              columns={recordColumns}
              dataSource={filteredFundRecords}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: false,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} của ${total} biến động`,
              }}
              loading={recordsLoading}
              scroll={{ x: 1200 }}
            />
          </TabPane>
        </Tabs>
      </Card>

      <Modal
        title="Sửa thông tin chuyển tiền"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingTransfer(null);
          editForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditSubmit}>
          <Form.Item
            label="Số tiền chuyển"
            name="amount"
            rules={[
              { required: true, message: 'Vui lòng nhập số tiền' },
              {
                type: 'number',
                min: 1,
                message: 'Số tiền phải lớn hơn 0',
              },
              {
                validator: (_, value) => {
                  if (editingTransfer && value) {
                    const difference = value - editingTransfer.amount;
                    if (difference > currentBalance) {
                      return Promise.reject(
                        'Số dư không đủ để tăng số tiền chuyển'
                      );
                    }
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Nhập số tiền"
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
              addonAfter="đ"
            />
          </Form.Item>

          <Form.Item label="Mô tả" name="description">
            <Input.TextArea placeholder="Mô tả lý do chuyển tiền" rows={3} />
          </Form.Item>

          <Form.Item className="mb-0 text-right">
            <Space>
              <Button
                onClick={() => {
                  setEditModalVisible(false);
                  setEditingTransfer(null);
                  editForm.resetFields();
                }}
              >
                Hủy
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Cập nhật
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default TransferHistory;
